import { Form, Formik, FormikValues } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import * as Yup from 'yup'
import Headline from '../Components/Elements/Headline'
import Button from '../Components/Formik/Button'
import Input from '../Components/Formik/Input'
import Select from '../Components/Formik/Select'
import { CURRENCY_NAMES, Currency } from '../lib/Enums/Currency'
import { Language } from '../lib/Enums/Language'
import { useAuthenticatedUser, useSaveUser } from '../lib/Hooks/User'
import { isCurrency } from '../lib/Validations'

export default function ProfilePage() {
  const { t } = useTranslation(['profile', 'common'])
  const { data: authenticatedUser } = useAuthenticatedUser()
  const { mutateAsync: saveUser } = useSaveUser()
  const [isLoading, toggleLoading] = useState(false)

  if (!authenticatedUser) {
    return <Navigate to='/login' />
  }

  return (
    <>
      <Headline className='mb-5'>{t('profile:title')}</Headline>
      <Formik
        initialValues={{
          name: authenticatedUser.name,
          email: authenticatedUser.email,
          phone: authenticatedUser.phone ?? '',
          language_interface: authenticatedUser.language_interface,
          currency: authenticatedUser.currency,
          salary: authenticatedUser.data.salary
            ? authenticatedUser.data.salary.toString().replaceAll('.', ',')
            : '',
        }}
        validationSchema={Yup.object({
          name: Yup.string().required(t('common:validation.required')),
          email: Yup.string().required(t('common:validation.required')),
          salary: Yup.string()
            .transform((currentValue) => {
              // First remove all danish thousand seperators (.) and then replace the danish delimiter seperator (,) with the english one (.).
              // This makes it's possible to save floats.
              return currentValue.replaceAll('.', '').replaceAll(',', '.')
            })
            .test(
              isCurrency.name,
              t('common:validation.invalidAmount'),
              isCurrency.test,
            ),
        })}
        onSubmit={async (values: FormikValues) => {
          toggleLoading(true)

          const salary = values.salary.replaceAll('.', '').replaceAll(',', '.')

          const newUser = {
            ...authenticatedUser,
            name: values.name,
            phone: values.phone,
            language_interface: values.language_interface,
            currency: values.currency,
            data: { ...authenticatedUser.data, salary },
          }
          await saveUser(newUser)
          toggleLoading(false)

          if (values.language_interface !== authenticatedUser.language_interface) {
            window.location.reload()
          }
        }}
      >
        <Form>
          <Headline size='lg' className='mb-5'>
            {t('profile:account.heading')}
          </Headline>
          <p className='mb-2'>{t('profile:account.name')}</p>
          <Input
            name='name'
            placeholder={t('profile:account.name')}
            className='mb-5'
          />
          <p className='mb-2'>{t('profile:account.email')}</p>
          <Input
            name='email'
            placeholder={t('profile:account.email')}
            className='mb-5'
            disabled
          />
          <p className='mb-2'>{t('profile:account.phone')}</p>
          <Input
            name='phone'
            placeholder={t('profile:account.phone')}
            className='mb-5'
          />
          <p className='mb-2'>{t('profile:account.language')}</p>
          <Select name='language_interface' className='mb-5'>
            <option value={Language.DANISH}>{t('profile:languages.da')}</option>
            <option value={Language.ENGLISH}>
              {t('profile:languages.en')}
            </option>
            <option value={Language.GERMAN}>{t('profile:languages.de')}</option>
          </Select>
          <p className='mb-2'>{t('profile:account.currency')}</p>
          <Select name='currency' className='mb-5'>
            {Object.values(Currency).map((currency) => (
              <option key={currency} value={currency}>
                {currency} — {CURRENCY_NAMES[currency]}
              </option>
            ))}
          </Select>
          <Button
            type='submit'
            disabled={isLoading}
            loading={isLoading}
            showLoadingFeedback
          >
            {t('profile:account.save')}
          </Button>
        </Form>
      </Formik>
    </>
  )
}
